import { FC, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { Filter } from "constants/filters";
import { ToastContent, getToastContent } from "constants/toasts";
import { sortAgents } from "helpers/agent";
import { IAgent, selectAgents } from "store/entities/agents";
import { selectAgentsInFiltersLimit } from "store/entities/application-settings/selectors";
import { ToastVariant, ToastsActions } from "store/features/toasts";

import { createRemovedAgentOption } from "../helpers";
import { IFilterUpdate } from "../interfaces";
import { withFilterBase } from "../withFilterBase";

import { createAgentOption } from "./agent-filter";

interface IProps {
  filterValues: string[];
  agents: IAgent[];
  currentAgent: IAgent;
  onChange(filterUpdate: IFilterUpdate<string[] | null>): void;
  multiple?: boolean;
}

const AgentFilter: FC<IProps> = props => {
  const maxAgentsInFilters = useSelector(selectAgentsInFiltersLimit);
  const dispatch = useDispatch();
  const agentsById = useSelector(selectAgents);
  const { agents, currentAgent, onChange, filterValues, multiple } = props;

  const handleChange = (selectedItem: IPickerListItem[] | null): void => {
    if (selectedItem && selectedItem.length > maxAgentsInFilters) {
      dispatch(
        ToastsActions.createToast({
          variant: ToastVariant.Error,
          content: getToastContent(ToastContent.AGENTS_LIMIT_IN_FILTERS_REACHED, { limit: maxAgentsInFilters }),
        })
      );
      return;
    }

    const isEmpty = !selectedItem || selectedItem.length === 0;

    onChange({
      name: Filter.Agent,
      value: isEmpty ? null : (selectedItem?.map(item => item.key) ?? []),
    });
  };

  const agentOptions = useMemo(
    () => agents.sort(sortAgents("name", false, currentAgent)).map(agent => createAgentOption(agent, currentAgent)),
    [agents, currentAgent]
  );

  const selectedAgents = useMemo(
    () =>
      filterValues?.map(agentId => {
        const agent = agentsById[agentId];

        return agent ? createAgentOption(agent, currentAgent) : createRemovedAgentOption(agentId);
      }),
    [agents, filterValues]
  );

  return (
    <Picker
      id="agent-filter"
      openedOnInit
      placeholder="Select"
      options={agentOptions}
      type={multiple ? "multi" : "single"}
      selected={selectedAgents}
      onSelect={handleChange}
      clearSearchAfterSelection
      hideClearButton
    />
  );
};

export default withFilterBase(AgentFilter);
